/**
 * A CARD SEEN FROM THE BACK — something that is here and is not yours to see yet.
 *
 * One object, three variants, and the difference is what a gesture could possibly do:
 *
 *   static   the partner's sealed agenda. It stands for a thing that never arrives on this client
 *            at all, so it has no press and no accessibility action: there is nothing a gesture
 *            could do. min-height 104.
 *   locked   the week's result BEFORE Sunday evening. The same back at the lifting variant's own
 *            height, with a padlock, the door's name and the countdown to it on its face — so the
 *            thing you will lift is standing there all week with a clock on it, rather than being
 *            a grey line of body text that appears where a card is going to be. Not pressable:
 *            nothing it could do has opened yet, and a control that refuses is worse than an
 *            object that is visibly not ready.
 *   lifting  the week's unrevealed result, once the window is open. min-height 136, an arrow and
 *            "Move up to reveal", and it answers an 80px upward drag OR a plain tap.
 *
 * Face down means WITHHELD. It is never used for content that is merely loading — a skeleton says
 * "this is coming", a hatched back says "this exists and you cannot have it", and a reader who has
 * learned the second reading will wait forever in front of the first.
 *
 * Drawn rather than shipped as an asset: a hatch of rotated Views costs nothing, needs no
 * dependency, and takes the theme's own border colour in both modes instead of baking a
 * light-mode pattern into a dark screen. The stripe block is inset far outside the card on every
 * edge so that rotating it about its own centre still covers every corner; the card's
 * `overflow-hidden` crops it back.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { Animated, PanResponder, Pressable, StyleSheet, View } from 'react-native';

/** How far up the card has to travel before the thing under it is actually revealed. */
const THRESHOLD = 80;
/** How far it flies once it has been. Past the top of any card it could be sitting in. */
const AWAY = 520;
/**
 * How long that flight takes — and how long ANY reveal in this app takes.
 *
 * Exported because Date Night's scratch is the same gesture on a different object: a face-down
 * thing lifts, turns over, and lands as the open card. Two reveals a fifth of a second apart in
 * length are two different apps, so the number is shared rather than retyped.
 */
export const FLIGHT_MS = 320;

/**
 * The hatch: 3px stripes at a 14px pitch, rotated 45°.
 *
 * 72 of them, which is enough to cross the widest card this app draws once the block has been
 * inset far enough to survive the rotation.
 */
const STRIPES = Array.from({ length: 72 });
const STRIPE_GAP = { marginTop: 14 } as const;
const HATCH_BLOCK = {
  position: 'absolute',
  left: -300,
  right: -300,
  top: -300,
  bottom: -300,
  transform: [{ rotate: '45deg' }],
} as const;

/** What the lifting variant needs to know. Its absence is what makes the card static. */
export interface Lift {
  /** The heading on the back. "Move up to reveal". */
  label: string;
  /** The line under it, saying what is face down. */
  body?: string;
  /** For the screen reader: what activating this does. */
  hint: string;
  /** The accessible name of the whole card. */
  name: string;
  /**
   * The action. Called exactly once per gesture; the card animates away only once this RESOLVES,
   * so a reveal that fails leaves the card where it was rather than a hole where the result would
   * have been. It reports its own errors and throws.
   */
  onReveal: () => Promise<void>;
  /** Called once the card is out of the way. The caller refreshes here. */
  onRevealed: () => void;
}

/**
 * What the locked variant needs to know: the door, and how long until it opens.
 *
 * `countdown` is a phrase and not an instant, because the caller already owns the app's one way of
 * writing a duration in words and a second copy of that arithmetic in here is a second copy to get
 * wrong. Null is a real answer — "any moment now" — and the card simply drops the line.
 */
export interface Locked {
  /** The heading on the back. "Reveal opens Sunday evening". */
  label: string;
  /** The figure on its face: "in 3 days 2 hours", or null when it is closer than a minute. */
  countdown?: string | null;
  /** The line under it, saying what to do with the week meanwhile. */
  body?: string;
}

export function FaceDownCard({
  className,
  lift,
  locked,
  children,
}: {
  className?: string;
  /** Present for the lifting variant, absent for the other two. */
  lift?: Lift;
  /** Present for the locked variant. Ignored when `lift` is given — an open door wins. */
  locked?: Locked;
  /** The static variant's caption. Ignored by the other two, which draw their own. */
  children?: React.ReactNode;
}) {
  if (lift) return <Lifting lift={lift} className={className} />;
  if (locked) {
    return (
      // The lifting variant's own height, deliberately: this card BECOMES that one on Sunday, and
      // a slot that changes size at the moment the door opens reads as a different object arriving.
      <Back className={cn('min-h-[136px]', className)}>
        {/* No arrow, and that is the whole difference at a glance: the lifting variant wears an ↑
            because it can be lifted, and a mark on this one would be an invitation the card cannot
            honour yet. The hatch already says withheld. */}
        <Text variant="heading" className="text-center">
          {locked.label}
        </Text>
        {locked.countdown === undefined ? null : (
          // The countdown is the figure on the card's face, so it is set in the display cut with
          // tabular figures: it is the one number here, and it changes under you while you look.
          <Text variant="small" className="font-display tabular-nums text-body text-muted-foreground font-semibold">
            {locked.countdown ? `in ${locked.countdown}` : 'any moment now'}
          </Text>
        )}
        {locked.body ? (
          <Text variant="small" className="text-muted-foreground text-center">
            {locked.body}
          </Text>
        ) : null}
      </Back>
    );
  }
  return <Back className={cn('min-h-[104px]', className)}>{children}</Back>;
}

/**
 * THE HATCH ITSELF, as an absolutely-positioned fill for anything that wants it.
 *
 * Exported because it is the app's one mark for WITHHELD, and it is needed in two places: on the
 * back of a card, and over the Thanks veil — the partner's appreciation, present and not yours to
 * read until you have written your own. That used to be three grey bars, which is the shape a
 * SKELETON has, and a reader who has learned "grey bars mean loading" waits in front of a thing
 * that is not coming until they act.
 *
 * The parent must clip it (`overflow-hidden`) and give it something to sit in: the block is inset
 * 300px outside on every edge so that rotating it about its own centre still covers the corners.
 */
export function Hatch() {
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFillObject}>
      <View style={HATCH_BLOCK}>
        {STRIPES.map((_, i) => (
          <View key={i} className="bg-border h-[3px]" style={i ? STRIPE_GAP : undefined} />
        ))}
      </View>
    </View>
  );
}

/**
 * The surface: `card-2` with a hairline, and the hatch across it.
 *
 * 8px, not the card's 10: both sheets cut `.facedown` at --radius-sm, and it is drawn beside the
 * sealed panel on the agenda card, where two nearly-equal radii read as a mistake.
 */
function Back({ className, children }: { className?: string; children?: React.ReactNode }) {
  return (
    <View className={cn('bg-card-2 border-border overflow-hidden rounded-md border', className)}>
      <Hatch />
      <View className="grow items-center justify-center gap-1 p-4">{children}</View>
    </View>
  );
}

/**
 * The lifting variant, gesture and all.
 *
 * A button labelled "Run the Sunday reveal" is an administrative act — one of them presses it,
 * alone, and the other reads the outcome later. A card you physically lift is the same POST with a
 * moment attached to it: it takes a deliberate movement, it can be done on a couch with two people
 * watching one phone, and it cannot happen by a thumb brushing the screen on the way past.
 */
function Lifting({ lift, className }: { lift: Lift; className?: string }) {
  const [busy, setBusy] = React.useState(false);

  /**
   * How far the card has been lifted, in pixels, negative being up.
   *
   * A ref rather than state: the finger moves it sixty times a second and none of those frames is
   * a render. `useNativeDriver` stays FALSE deliberately — react-native-web has no native driver
   * and passing true there only earns a console warning.
   */
  const y = React.useRef(new Animated.Value(0)).current;
  /** The latest handler, so the responder below can be built once and never rebuilt. */
  const revealNow = React.useRef<() => void>(() => {});
  const busyRef = React.useRef(false);

  const reveal = React.useCallback(async () => {
    // A second gesture while the first is in flight is ignored, not queued: the post is not
    // idempotent and a double reveal is a double payout.
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await lift.onReveal();
      // POST first, THEN the flight — the card leaving is the result arriving, so it must not
      // start before the server has agreed there is one.
      Animated.timing(y, { toValue: -AWAY, duration: FLIGHT_MS, useNativeDriver: false }).start(() => {
        lift.onRevealed();
      });
    } catch {
      // The caller has already reported it. The card comes back.
      Animated.spring(y, { toValue: 0, useNativeDriver: false, bounciness: 6 }).start();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [lift, y]);

  React.useEffect(() => {
    revealNow.current = () => void reveal();
  });

  const pan = React.useRef(
    PanResponder.create({
      // Capture, not the plain `onMoveShouldSet`: the card's own Pressable takes the responder on
      // touch-down (it has to, for the tap), so the drag has to be claimed off it on the first real
      // upward movement. A press that never moves stays a press.
      onMoveShouldSetPanResponderCapture: (_e, g) => g.dy < -8,
      onPanResponderMove: (_e, g) => y.setValue(Math.min(0, g.dy)),
      onPanResponderRelease: (_e, g) => {
        if (g.dy <= -THRESHOLD) revealNow.current();
        else Animated.spring(y, { toValue: 0, useNativeDriver: false, bounciness: 6 }).start();
      },
      onPanResponderTerminate: () => {
        Animated.spring(y, { toValue: 0, useNativeDriver: false }).start();
      },
    }),
  ).current;

  return (
    <Animated.View
      {...pan.panHandlers}
      style={{
        transform: [{ translateY: y }],
        // Full strength through the whole drag — the fade belongs to the card leaving, not to the
        // gesture, or an aborted lift would read as the result dimming for no reason.
        opacity: y.interpolate({
          inputRange: [-AWAY, -THRESHOLD, 0],
          outputRange: [0, 1, 1],
          extrapolate: 'clamp',
        }),
      }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={lift.name}
        accessibilityHint={lift.hint}
        accessibilityState={{ busy }}
        disabled={busy}
        // The tap is not a fallback for a drag that missed. It is the WHOLE of this control for
        // anyone driving it with a keyboard, a switch or a screen reader, none of which produce a
        // dy — so it does exactly what an 80px lift does and is never the lesser path.
        onPress={() => revealNow.current()}>
        <Back className={cn('min-h-[136px]', className)}>
          <Text variant="caption" className="text-muted-foreground">
            ↑
          </Text>
          <Text variant="heading">{lift.label}</Text>
          {lift.body ? (
            <Text variant="small" className="text-muted-foreground text-center">
              {lift.body}
            </Text>
          ) : null}
        </Back>
      </Pressable>
    </Animated.View>
  );
}
